/*
 * CRYPSEY LANDING PAGE
 * Last Updated: 2025-01-27
 *
 * Recent Changes:
 * - 2025-01-27: Removed all investor-focused content, updated to product-focused messaging
 * - 2025-01-27: Aligned typography, spacing, and colors with design system rules
 * - 2025-01-27: Fixed hero section, problem cards, and pricing structure
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  ArrowRight,
  BarChart3,
  Calendar,
  Check,
  CheckCircle,
  ChevronRight,
  ChevronDown,
  Code,
  Database,
  Download,
  Globe,
  LineChart,
  Lock,
  Network,
  Shield,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  Zap,
  X,
  Menu,
} from "lucide-react";
import MortgageChainFooter from "../../components/MortgageChainFooter/MortgageChainFooter";
import ContactModal, {
  ContactFormData,
} from "../../components/MortgageChainLanding";
import { ContactEmailService } from "../../Api/ContactEmailService";

type FormType = "trial" | "demo" | "call";

type GhostAuthor = {
  name: string;
};

type GhostTag = {
  name: string;
};

type GhostPost = {
  id: string;
  title: string;
  excerpt?: string | null;
  custom_excerpt?: string | null;
  url?: string;
  slug: string;
  published_at?: string | null;
  reading_time?: number | null;
  primary_author?: GhostAuthor | null;
  primary_tag?: GhostTag | null;
  feature_image?: string | null;
};

type GhostPostsResponse = {
  posts?: GhostPost[];
};

type MousePosition = {
  x: number;
  y: number;
};

type VisibilityMap = Record<string, boolean>;

declare global {
  interface WindowEventMap {
    "mortgagechain:openForm": CustomEvent<{ type: FormType }>;
  }
}

const SCROLL_SECTIONS = [
  "hero",
  "problem",
  "solution",
  "how-it-works",
  "use-cases",
  "technology",
  "pricing",
  "api",
  "blog",
] as const;

// Safe to use in frontend (Ghost Content API key)
const GHOST_API_URL = "https://blog.crypsey.com";
const GHOST_CONTENT_KEY = "424e539e210bffc65eec193856";

async function getGhostBlogs(signal?: AbortSignal): Promise<GhostPost[]> {
  try {
    const url = `${GHOST_API_URL}/ghost/api/content/posts/?key=${GHOST_CONTENT_KEY}&include=tags,authors&limit=15`;
    console.log("[Blog] Fetching from:", url);

    const response = await fetch(url, { signal });

    console.log(
      "[Blog] Response status:",
      response.status,
      response.statusText
    );

    if (!response.ok) {
      const errorText = await response.text().catch(() => "");
      console.error("[Blog] API error response:", errorText);
      throw new Error(
        `Ghost Content API request failed (${response.status}): ${response.statusText}`
      );
    }

    const data = (await response.json()) as GhostPostsResponse;
    console.log("[Blog] Received data:", {
      hasPosts: !!data.posts,
      postsCount: Array.isArray(data.posts) ? data.posts.length : 0,
      dataKeys: Object.keys(data),
    });

    const posts = Array.isArray(data.posts) ? data.posts : [];
    console.log("[Blog] Returning", posts.length, "posts");
    return posts;
  } catch (error) {
    console.error("[Blog] Error fetching blogs:", error);
    throw error;
  }
}

const AnimatedCounter: React.FC<{ target: number; suffix?: string }> = ({
  target,
  suffix = "",
}) => {
  const [count, setCount] = useState(0);
  const elementRef = useRef<HTMLSpanElement | null>(null);
  const hasAnimatedRef = useRef(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    let animationFrameId: number | null = null;
    let startTime: number | null = null;
    const duration = 2000;

    const animate = (timestamp: number) => {
      if (startTime === null) {
        startTime = timestamp;
      }

      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);

      setCount(Math.round(target * eased));

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        setCount(target);
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimatedRef.current) {
          hasAnimatedRef.current = true;
          animationFrameId = requestAnimationFrame(animate);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [target]);

  return (
    <span ref={elementRef}>
      {count.toLocaleString("en-US")}
      {suffix}
    </span>
  );
};

const NetworkVisualization: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvasElement = canvasRef.current;
    if (!canvasElement) return;

    const context = canvasElement.getContext("2d");
    if (!context) return;

    const element = canvasElement;
    const ctx = context;

    let animationFrameId: number;

    const particleCount = 100;
    const connectionDistance = 150;
    const particles: Particle[] = [];

    class Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
      opacity: number;

      constructor() {
        this.x = Math.random() * element.width;
        this.y = Math.random() * element.height;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.radius = Math.random() * 2 + 1;
        this.opacity = Math.random() * 0.5 + 0.3;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        if (this.x < 0 || this.x > element.width) {
          this.vx *= -1;
        }
        if (this.y < 0 || this.y > element.height) {
          this.vy *= -1;
        }
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 212, 255, ${this.opacity})`;
        ctx.fill();
      }
    }

    const resizeCanvas = () => {
      element.width = element.offsetWidth;
      element.height = element.offsetHeight;
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    for (let i = 0; i < particleCount; i += 1) {
      particles.push(new Particle());
    }

    const render = () => {
      ctx.clearRect(0, 0, element.width, element.height);

      particles.forEach((particle, index) => {
        particle.update();
        particle.draw();

        for (let j = index + 1; j < particles.length; j += 1) {
          const dx = particles[j].x - particle.x;
          const dy = particles[j].y - particle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < connectionDistance) {
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(particles[j].x, particles[j].y);
            const opacity = (1 - distance / connectionDistance) * 0.3;
            ctx.strokeStyle = `rgba(0, 212, 255, ${opacity})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      });

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      window.removeEventListener("resize", resizeCanvas);
    };
  }, []);

  return (
    <div className="relative w-full h-full">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ width: "100%", height: "100%" }}
      />
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0, 212, 255, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0, 212, 255, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
            animation: "gridMove 20s linear infinite",
          }}
        />
      </div>
      <div className="absolute inset-0 opacity-5">
        {Array.from({ length: 20 }).map((_, index) => (
          <div
            key={`hex-${index}`}
            className="absolute border border-cyan-400"
            style={{
              width: "80px",
              height: "92px",
              clipPath:
                "polygon(30% 0%, 70% 0%, 100% 50%, 70% 100%, 30% 100%, 0% 50%)",
              left: `${(index % 5) * 20}%`,
              top: `${Math.floor(index / 5) * 25}%`,
              animation: `pulse ${2 + (index % 3)}s ease-in-out infinite`,
              animationDelay: `${index * 0.2}s`,
            }}
          />
        ))}
      </div>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
        <div className="relative w-64 h-64">
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-cyan-500 to-purple-500 opacity-20 blur-3xl animate-pulse" />
          <div
            className="absolute inset-8 rounded-full bg-gradient-to-r from-cyan-400 to-purple-400 opacity-30 blur-2xl"
            style={{ animation: "pulse 3s ease-in-out infinite" }}
          />
          <div
            className="absolute inset-16 rounded-full bg-cyan-400 opacity-40 blur-xl"
            style={{ animation: "pulse 4s ease-in-out infinite" }}
          />
          <div
            className="absolute inset-0 border-2 border-cyan-500/30 rounded-full"
            style={{ animation: "spin 20s linear infinite" }}
          />
          <div
            className="absolute inset-8 border-2 border-purple-500/30 rounded-full"
            style={{ animation: "spin 15s linear infinite reverse" }}
          />
        </div>
      </div>
      {Array.from({ length: 8 }).map((_, index) => (
        <div
          key={`orbit-${index}`}
          className="absolute w-3 h-3 bg-cyan-400 rounded-full shadow-lg shadow-cyan-400/50"
          style={{
            left: "50%",
            top: "50%",
            animation: `orbit ${10 + index * 2}s linear infinite`,
            animationDelay: `${index * 0.5}s`,
            transformOrigin: `${80 + index * 20}px center`,
          }}
        />
      ))}
      {Array.from({ length: 6 }).map((_, index) => (
        <div
          key={`stream-${index}`}
          className="absolute h-px bg-gradient-to-r from-transparent via-cyan-400 to-transparent"
          style={{
            width: "200px",
            left: `${10 + index * 15}%`,
            top: `${20 + index * 12}%`,
            opacity: 0.3,
            animation: `slideRight ${3 + index}s ease-in-out infinite`,
            animationDelay: `${index * 0.8}s`,
          }}
        />
      ))}
      <svg className="absolute inset-0 w-full h-full opacity-20">
        {Array.from({ length: 12 }).map((_, index) => {
          const x1 = 20 + (index % 4) * 20;
          const y1 = 20 + Math.floor(index / 4) * 25;
          const x2 = 40 + ((index + 1) % 4) * 20;
          const y2 = 40 + Math.floor((index + 1) / 4) * 25;

          return (
            <line
              key={`line-${index}`}
              x1={`${x1}%`}
              y1={`${y1}%`}
              x2={`${x2}%`}
              y2={`${y2}%`}
              stroke="url(#gradient)"
              strokeWidth="2"
              opacity="0.5"
            >
              <animate
                attributeName="stroke-opacity"
                values="0.2;0.8;0.2"
                dur={`${2 + (index % 3)}s`}
                repeatCount="indefinite"
              />
            </line>
          );
        })}
        <defs>
          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#00D4FF" />
            <stop offset="100%" stopColor="#8B5CF6" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
};

const colorToHex: Record<"cyan" | "purple" | "green" | "amber", `#${string}`> =
  {
    cyan: "#00D4FF",
    purple: "#8B5CF6",
    green: "#00FF88",
    amber: "#FFB800",
  };

export default function MortgageChainLanding() {
  const navigate = useNavigate();
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] =
    useState<(typeof SCROLL_SECTIONS)[number]>("hero");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formType, setFormType] = useState<FormType>("trial");
  const [mousePosition, setMousePosition] = useState<MousePosition>({
    x: 0,
    y: 0,
  });
  const [isVisible, setIsVisible] = useState<VisibilityMap>({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [blogPosts, setBlogPosts] = useState<GhostPost[]>([]);
  const [isBlogLoading, setIsBlogLoading] = useState(true);
  const [blogError, setBlogError] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      const maxScroll =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = maxScroll > 0 ? window.scrollY / maxScroll : 0;
      setScrollProgress(Math.min(Math.max(progress, 0), 1));

      const currentSection = SCROLL_SECTIONS.find((section) => {
        const element = document.getElementById(section);
        if (!element) return false;
        const { top, bottom } = element.getBoundingClientRect();
        return top <= 100 && bottom >= 100;
      });

      if (currentSection && currentSection !== activeSection) {
        setActiveSection(currentSection);
      }
    };

    const handleMouseMove = (event: MouseEvent) => {
      setMousePosition({
        x: (event.clientX / window.innerWidth) * 20 - 10,
        y: (event.clientY / window.innerHeight) * 20 - 10,
      });
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("mousemove", handleMouseMove);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [activeSection]);

  useEffect(() => {
    const elements = document.querySelectorAll<HTMLElement>("[data-animate]");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const targetId = entry.target.getAttribute("id");
            if (targetId) {
              setIsVisible((prev) => ({
                ...prev,
                [targetId]: true,
              }));
            }
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 }
    );

    elements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [blogPosts.length]); // Re-run when blog posts are loaded

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      try {
        setIsBlogLoading(true);
        setBlogError(null);
        const posts = await getGhostBlogs(controller.signal);
        console.log("[Blog] Setting blog posts:", posts.length);
        setBlogPosts(posts);
        if (posts.length === 0) {
          console.warn("[Blog] No posts returned from API");
          setBlogError("No blog posts available at this time.");
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error("[Blog] Failed to fetch Ghost blog posts:", error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load blog posts. Please try again later.";
        setBlogError(errorMessage);
      } finally {
        if (controller.signal.aborted) return;
        setIsBlogLoading(false);
      }
    })();

    return () => controller.abort();
  }, []);

  const openForm = useCallback(
    (type: FormType) => {
      setFormType(type);
      setIsFormOpen(true);
    },
    [setFormType, setIsFormOpen]
  );

  const closeForm = useCallback(() => {
    setIsFormOpen(false);
  }, []);

  const handleFormSubmit = useCallback(async (formData: ContactFormData) => {
    try {
      // Send email via AWS API
      await ContactEmailService.sendContactEmail({
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone || undefined,
        companyName: formData.companyName || undefined,
        useCase: formData.useCase || undefined,
        type: formData.type,
      });

      // Close form and show success message
      setIsFormOpen(false);
      setShowSuccessModal(true);

      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }

      successTimeoutRef.current = setTimeout(() => {
        setShowSuccessModal(false);
      }, 5000);
    } catch (error) {
      console.error("Failed to submit contact form:", error);
      // You can add error handling UI here if needed
      alert(
        "Failed to submit your request. Please try again or contact us directly at patrick@crypsey.com"
      );
    }
  }, []);

  useEffect(() => {
    const handler = (event: CustomEvent<{ type: FormType }>) => {
      const nextType = event.detail?.type;
      if (nextType === "call" || nextType === "demo" || nextType === "trial") {
        openForm(nextType);
      }
    };

    window.addEventListener("mortgagechain:openForm", handler);

    return () => {
      window.removeEventListener("mortgagechain:openForm", handler);
    };
  }, [openForm]);

  const buttonGlowStyle = useMemo(
    () => ({
      transform: `translate(${mousePosition.x * 0.5}px, ${
        mousePosition.y * 0.5
      }px)`,
      transition: "transform 0.3s ease-out",
    }),
    [mousePosition.x, mousePosition.y]
  );

  const contentParallaxStyle = useMemo(
    () => ({
      transform: `translate(${mousePosition.x * -0.5}px, ${
        mousePosition.y * -0.5
      }px)`,
      transition: "transform 0.3s ease-out",
    }),
    [mousePosition.x, mousePosition.y]
  );

  const keyStats = [
    {
      value: 5000,
      suffix: "+",
      label: "Mortgage Originators",
      color: "cyan",
      Icon: Users,
    },
    {
      value: 300,
      suffix: "+",
      label: "Private Lenders",
      color: "purple",
      Icon: Network,
    },
    {
      value: 50,
      suffix: "+",
      label: "PropTech Companies",
      color: "green",
      Icon: Sparkles,
    },
  ] as const;

  const tractionStats = [
    {
      Icon: Users,
      title: "Beta Customers",
      value: "12",
      description: "Private lenders and mortgage funds actively using platform",
      color: "cyan",
      trend: "+3 this month",
      delay: "0.1s",
    },
    {
      Icon: Database,
      title: "Loan Database",
      value: "10K+",
      description: "Anonymized mortgages aggregated and verified",
      color: "purple",
      trend: "+2.5K this quarter",
      delay: "0.2s",
    },
    {
      Icon: Lock,
      title: "Data Partners",
      value: "8",
      description: "Brokers and lenders committed to data sharing",
      color: "green",
      trend: "5 more signed",
      delay: "0.3s",
    },
    {
      Icon: TrendingUp,
      title: "Monthly Growth",
      value: "35%",
      description: "MoM increase in data volume and queries",
      color: "amber",
      trend: "Accelerating",
      delay: "0.4s",
    },
  ] as const;

  const pricingTiers = [
    {
      name: "Starter",
      price: "$499",
      period: "/mo",
      description: "Perfect for small teams",
      features: [
        { text: "100 queries/month", included: true },
        { text: "Basic reports", included: true },
        { text: "Email support", included: true },
        { text: "7-day free trial", included: true },
      ],
      cta: "Start Free Trial",
      Icon: Users,
      color: "cyan" as const,
      delay: "0.1s",
      highlighted: false,
    },
    {
      name: "Professional",
      price: "$1,999",
      period: "/mo",
      description: "Most popular choice",
      features: [
        { text: "1,000 queries/month", included: true },
        { text: "Full API access", included: true },
        { text: "Custom reports (2/mo)", included: true },
        { text: "Priority support", included: true },
      ],
      cta: "Start Free Trial",
      highlighted: true,
      Icon: TrendingUp,
      color: "purple" as const,
      delay: "0.2s",
      badge: "MOST POPULAR",
    },
    {
      name: "Business",
      price: "$4,999",
      period: "/mo",
      description: "For growing companies",
      features: [
        { text: "Unlimited queries", included: true },
        { text: "Advanced analytics", included: true },
        { text: "10 custom reports/mo", included: true },
        { text: "Dedicated support", included: true },
      ],
      cta: "Start Free Trial",
      Icon: Shield,
      color: "green" as const,
      delay: "0.3s",
      highlighted: false,
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      description: "Tailored solutions",
      features: [
        { text: "White-label platform", included: true },
        { text: "Data integration", included: true },
        { text: "Success manager", included: true },
        { text: "SLA guarantees", included: true },
      ],
      cta: "Contact Sales",
      Icon: Shield,
      color: "cyan" as const,
      delay: "0.4s",
      highlighted: false,
    },
  ];

  const formatBlogDate = useCallback((dateString?: string | null) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "";
    return date.toISOString().slice(0, 10);
  }, []);

  const blogCards = useMemo(() => {
    console.log("[Blog] Computing blogCards from", blogPosts.length, "posts");
    const cards = blogPosts.slice(0, 6).map((post, index) => {
      const color =
        index % 3 === 0 ? "cyan" : index % 3 === 1 ? "purple" : "green";
      const excerpt = post.excerpt || post.custom_excerpt || "";
      const author = post.primary_author?.name || "Crypsey Team";
      const category = post.primary_tag?.name || "Insights";
      const date = formatBlogDate(post.published_at);
      const readTime =
        typeof post.reading_time === "number" && post.reading_time > 0
          ? `${post.reading_time} min read`
          : "";
      const href = post.url || `${GHOST_API_URL}/${post.slug}/`;

      return {
        id: post.id,
        title: post.title,
        excerpt,
        author,
        date,
        readTime,
        category,
        color,
        href,
        featureImage: post.feature_image || null,
        delay: `${0.1 + index * 0.1}s`,
      } as const;
    });
    console.log("[Blog] Generated", cards.length, "blog cards");
    return cards;
  }, [blogPosts, formatBlogDate]);

  // Immediately check visibility for blog cards when they're rendered
  useEffect(() => {
    if (blogCards.length > 0) {
      // Use setTimeout to ensure DOM is updated after render
      const timer = setTimeout(() => {
        blogCards.forEach((card) => {
          const element = document.getElementById(card.id);
          if (element) {
            const rect = element.getBoundingClientRect();
            // Check if element is partially visible in viewport
            const isPartiallyVisible =
              rect.top < window.innerHeight && rect.bottom > 0;

            if (isPartiallyVisible) {
              setIsVisible((prev) => ({
                ...prev,
                [card.id]: true,
              }));
              element.classList.add("visible");
            }
          }
        });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [blogCards]);

  return (
    <div className="bg-[#0A0E27] text-white min-h-screen overflow-x-hidden">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;600;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;600&family=Poppins:wght@500;600;700&display=swap');

        * {
          font-family: 'Inter', sans-serif;
        }

        h1, h2, h3 {
          font-family: 'Space Grotesk', sans-serif;
        }

        .font-mono {
          font-family: 'JetBrains Mono', monospace;
        }

        .btn-font {
          font-family: 'Poppins', sans-serif;
        }

        @keyframes gridMove {
          0% { transform: translateY(0); }
          100% { transform: translateY(50px); }
        }

        @keyframes float {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(20px, -20px); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }

        @keyframes glow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(0, 212, 255, 0.5),
                        0 0 40px rgba(0, 212, 255, 0.3),
                        0 0 60px rgba(0, 212, 255, 0.1);
          }
          50% {
            box-shadow: 0 0 30px rgba(0, 212, 255, 0.7),
                        0 0 60px rgba(0, 212, 255, 0.4),
                        0 0 90px rgba(0, 212, 255, 0.2);
          }
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes orbit {
          from { transform: rotate(0deg) translateX(0); }
          to { transform: rotate(360deg) translateX(0); }
        }

        @keyframes slideRight {
          0% { transform: translateX(-100%); opacity: 0; }
          50% { opacity: 0.5; }
          100% { transform: translateX(100%); opacity: 0; }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes shimmer {
          0% {
            background-position: -1000px 0;
          }
          100% {
            background-position: 1000px 0;
          }
        }

        .animate-fadeInUp {
          animation: fadeInUp 0.8s ease-out forwards;
        }

        .animate-fadeInLeft {
          animation: fadeInLeft 0.8s ease-out forwards;
        }

        .animate-fadeInRight {
          animation: fadeInRight 0.8s ease-out forwards;
        }

        .animate-scaleIn {
          animation: scaleIn 0.6s ease-out forwards;
        }

        [data-animate] {
          opacity: 0;
        }

        [data-animate].visible {
          opacity: 1;
        }

        .glassmorphic {
          background: rgba(26, 31, 58, 0.5);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(0, 212, 255, 0.2);
          border-radius: 16px;
        }

        .glassmorphic-strong {
          background: rgba(26, 31, 58, 0.95);
          backdrop-filter: blur(30px);
          border: 1px solid rgba(0, 212, 255, 0.3);
        }

        .gradient-text {
          background: linear-gradient(90deg, #00D4FF 0%, #8B5CF6 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .gradient-text-green {
          background: linear-gradient(90deg, #00FF88 0%, #00D4FF 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hover-glow {
          transition: all 0.3s ease;
        }

        .hover-glow:hover {
          animation: glow 2s ease-in-out infinite;
          transform: translateY(-4px);
        }

        .hover-lift {
          transition: all 0.3s ease;
        }

        .hover-lift:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(0, 212, 255, 0.3);
        }

        .scanline {
          position: relative;
          overflow: hidden;
        }

        .scanline::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(transparent 0%, rgba(255, 255, 255, 0.02) 50%, transparent 100%);
          background-size: 100% 4px;
          animation: scan 10s linear infinite;
          pointer-events: none;
        }

        @keyframes scan {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100%); }
        }

        .card-hover {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .card-hover:hover {
          transform: translateY(-12px);
          border-color: rgba(0, 212, 255, 0.6);
          box-shadow: 0 20px 60px rgba(0, 212, 255, 0.2);
        }

        .shimmer {
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(0, 212, 255, 0.3) 50%,
            transparent 100%
          );
          background-size: 200% 100%;
          animation: shimmer 3s ease-in-out infinite;
        }

        .text-glow {
          text-shadow: 0 0 20px rgba(0, 212, 255, 0.5),
                       0 0 40px rgba(0, 212, 255, 0.3);
        }

        .progress-bar {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, #00D4FF 0%, #8B5CF6 100%);
          transform-origin: left;
          z-index: 100;
        }

        html {
          scroll-behavior: smooth;
        }

        ::-webkit-scrollbar {
          width: 10px;
        }

        ::-webkit-scrollbar-track {
          background: #0A0E27;
        }

        ::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, #00D4FF 0%, #8B5CF6 100%);
          border-radius: 10px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, #00FF88 0%, #00D4FF 100%);
        }
      `}</style>

      <div
        className="progress-bar"
        style={{
          transform: `scaleX(${scrollProgress})`,
        }}
      />

      <section
        id="hero"
        className="relative min-h-screen flex items-center pt-28 scanline overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0E27] via-[#1A1F3A] to-[#2A1F4A]" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full filter blur-3xl opacity-20 animate-pulse" />
        <div
          className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full filter blur-3xl opacity-20 animate-pulse"
          style={{ animationDelay: "1s" }}
        />
        <div className="absolute inset-0 opacity-30">
          {Array.from({ length: 5 }).map((_, index) => (
            <div
              key={`bg-bubble-${index}`}
              className="absolute rounded-full"
              style={{
                width: `${200 + index * 100}px`,
                height: `${200 + index * 100}px`,
                left: `${20 + index * 15}%`,
                top: `${10 + index * 20}%`,
                background:
                  index % 2 === 0
                    ? "radial-gradient(circle, rgba(0, 212, 255, 0.1) 0%, transparent 70%)"
                    : "radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)",
                animation: `float ${10 + index * 2}s ease-in-out infinite`,
                animationDelay: `${index * 0.5}s`,
              }}
            />
          ))}
        </div>

        <div className="relative max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
          <div className="space-y-10 z-10" style={contentParallaxStyle}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/30 backdrop-blur-sm">
              <Sparkles className="w-4 h-4 text-cyan-400" />
              <span className="text-sm font-semibold text-cyan-400">
                AI-Powered | Real-Time Analytics
              </span>
            </div>

            <div className="space-y-6">
              <h1 className="text-[2.625rem] md:text-[3.5rem] font-bold leading-[1.1] tracking-tight">
                <span className="gradient-text text-glow">
                  AI-Powered Analytics
                </span>{" "}
                for Mortgage Data
              </h1>
              <p className="text-xl md:text-2xl text-gray-300">
                Real-time mortgage performance analytics for lenders and
                PropTech companies. Powered by AI and machine learning, not
                guesswork.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button
                type="button"
                onClick={() => navigate("/dashboard")}
                className="btn-font bg-gradient-to-r from-cyan-500 to-purple-500 px-10 py-5 rounded-2xl font-bold text-lg hover-glow flex items-center justify-center gap-3 group relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                <Zap className="w-5 h-5 relative z-10" />
                <span className="relative z-10">View Dashboard</span>
              </button>
              <a
                href="/docs"
                className="btn-font glassmorphic px-10 py-5 rounded-2xl font-bold text-lg hover:border-cyan-400 transition-all flex items-center justify-center gap-3 group"
              >
                <Code className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                View API Docs
              </a>
            </div>

            <div className="grid grid-cols-3 gap-4 pt-8">
              {[
                {
                  value: 2.1,
                  suffix: "T",
                  label: "Market Size",
                  Icon: Network,
                  color: "cyan",
                },
                {
                  value: 10,
                  suffix: "K+",
                  label: "Loans Tracked",
                  Icon: Database,
                  color: "purple",
                },
                {
                  value: 0,
                  suffix: "",
                  label: "Competition",
                  Icon: Target,
                  color: "green",
                },
              ].map(({ value, suffix, label, Icon, color }) => (
                <div
                  key={label}
                  className="glassmorphic rounded-2xl p-4 hover-lift group cursor-pointer text-center"
                >
                  <div className="text-2xl md:text-3xl font-bold font-mono gradient-text mb-2">
                    {value === 2.1 ? (
                      `$${value.toFixed(1)}${suffix}`
                    ) : value === 10 ? (
                      <>
                        <AnimatedCounter target={value} />
                        {suffix}
                      </>
                    ) : (
                      "Zero"
                    )}
                  </div>
                  <div className="text-xs text-gray-400">{label}</div>
                </div>
              ))}
            </div>
          </div>

          <div
            className="relative h-[500px] md:h-[700px]"
            style={buttonGlowStyle}
          >
            <NetworkVisualization />

            <div
              className="absolute top-10 left-10 glassmorphic rounded-2xl p-4 animate-fadeInLeft hover-lift"
              style={{ animationDelay: "0.5s" }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <div className="text-xs text-gray-400">Active Loans</div>
                  <div className="text-xl font-bold font-mono text-cyan-400">
                    10K+
                  </div>
                </div>
              </div>
            </div>

            <div
              className="absolute top-32 right-8 glassmorphic rounded-2xl p-4 animate-fadeInRight hover-lift"
              style={{ animationDelay: "0.7s" }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <div className="text-xs text-gray-400">Verified</div>
                  <div className="text-xl font-bold font-mono text-purple-400">
                    100%
                  </div>
                </div>
              </div>
            </div>

            <div
              className="absolute bottom-20 left-16 glassmorphic rounded-2xl p-4 animate-fadeInLeft hover-lift"
              style={{ animationDelay: "0.9s" }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <div className="text-xs text-gray-400">Response</div>
                  <div className="text-xl font-bold font-mono text-green-400">
                    &lt;200ms
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="flex flex-col items-center gap-2 text-gray-400 text-sm">
            <span>Scroll to explore</span>
            <ChevronRight className="w-5 h-5 rotate-90" />
          </div>
        </div>
      </section>

      <section id="problem" className="relative py-24 px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0A0E27]" />
        <div className="relative max-w-7xl mx-auto">
          <div
            data-animate
            id="problem-title"
            className={isVisible["problem-title"] ? "animate-fadeInUp" : ""}
          >
            <h2 className="text-[2rem] md:text-[3rem] font-bold text-center mb-4">
              A <span className="gradient-text">$2 Trillion</span> Market
              Operating Blind
            </h2>
            <p className="text-xl text-gray-400 text-center mb-16">
              Canada&apos;s mortgage industry lacks the data infrastructure that
              US institutions rely on
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                id: "problem-card-1",
                Icon: Database,
                title: "ZERO Data Platforms",
                description:
                  "Canadian mortgage lenders make critical decisions with zero real-time default rates, prepayment data, or market benchmarks.",
                highlight: "ZERO",
                highlightSubtitle: "Complete Market Blindness",
                color: "#FF3366",
                animationClass: "animate-fadeInLeft",
                delay: "0.1s",
              },
              {
                id: "problem-card-2",
                Icon: CheckCircle,
                title: "$3.5B US Market Solved",
                description:
                  "Black Knight ($1.4B revenue), CoreLogic ($2.1B revenue), 30+ established platforms serve every major US institution.",
                stats: [
                  { label: "Black Knight", value: "$1.4B" },
                  { label: "CoreLogic", value: "$2.1B" },
                ],
                color: "#00FF88",
                animationClass: "animate-fadeInUp",
                delay: "0.2s",
              },
              {
                id: "problem-card-3",
                Icon: TrendingUp,
                title: "Canada's Opportunity",
                description:
                  "5,000+ mortgage originators, 300+ private lenders, 50+ PropTech companies—all operating without comprehensive data.",
                highlight: "",
                highlightSubtitle: "",
                color: "#FFB800",
                animationClass: "animate-fadeInRight",
                delay: "0.3s",
              },
            ].map(
              ({
                id,
                Icon,
                title,
                description,
                highlight,
                highlightSubtitle,
                color,
                animationClass,
                delay,
                stats,
              }) => (
                <div
                  key={id}
                  data-animate
                  id={id}
                  className={`glassmorphic rounded-2xl p-8 card-hover group relative overflow-hidden ${
                    isVisible[id] ? animationClass : ""
                  }`}
                  style={{ animationDelay: delay }}
                >
                  {id === "problem-card-3" && (
                    <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-purple-500/10" />
                  )}
                  <div className="relative">
                    <div
                      className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform"
                      style={{
                        background: `${color}33`,
                      }}
                    >
                      <Icon className="w-8 h-8" style={{ color }} />
                    </div>
                    <h3 className="text-2xl font-bold mb-4" style={{ color }}>
                      {title}
                    </h3>
                    <p className="text-gray-300 leading-relaxed mb-6">
                      {description}
                    </p>
                    {stats ? (
                      <div className="mt-6 space-y-2">
                        {stats.map((item) => (
                          <div
                            key={item.label}
                            className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg hover:bg-green-500/20 transition-all"
                          >
                            <span className="text-sm text-gray-300">
                              {item.label}
                            </span>
                            <span className="font-bold font-mono text-green-400">
                              {item.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : highlight ? (
                      <div className="mt-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-center relative overflow-hidden">
                        <div className="absolute inset-0 shimmer opacity-20" />
                        <div
                          className="text-4xl font-bold font-mono text-glow"
                          style={{ color }}
                        >
                          {highlight}
                        </div>
                        {highlightSubtitle && (
                          <div
                            className="text-sm mt-2"
                            style={{ color: `${color}CC` }}
                          >
                            {highlightSubtitle}
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      </section>

      <section
        id="solution"
        className="relative py-24 px-6 bg-gradient-to-b from-[#0A0E27] to-[#1A1F3A]"
      >
        <div className="max-w-7xl mx-auto">
          <div
            data-animate
            id="solution-title"
            className={isVisible["solution-title"] ? "animate-fadeInUp" : ""}
          >
            <h2 className="text-[2rem] md:text-[3rem] font-bold text-center mb-4">
              The <span className="gradient-text">Crypsey</span> Platform
            </h2>
            <p className="text-xl text-gray-400 text-center mb-16">
              AI-powered mortgage data analytics, delivered through modern APIs
            </p>
          </div>

          <div
            data-animate
            id="platform-mockup"
            className={`mb-16 relative ${
              isVisible["platform-mockup"] ? "animate-scaleIn" : ""
            }`}
          >
            <div className="glassmorphic rounded-3xl p-8 transform hover:scale-[1.02] transition-transform duration-500 hover-lift">
              <div className="aspect-video bg-gradient-to-br from-[#1A1F3A] to-[#0A0E27] rounded-xl border border-cyan-500/30 flex items-center justify-center relative overflow-hidden group">
                <div className="absolute inset-0 opacity-20">
                  <div className="grid grid-cols-12 grid-rows-6 h-full gap-2 p-4">
                    {Array.from({ length: 72 }).map((_, index) => (
                      <div
                        key={`grid-cell-${index}`}
                        className="bg-cyan-500/30 rounded group-hover:bg-cyan-400/50 transition-all duration-300"
                        style={{
                          animation: `pulse ${
                            2 + (index % 3)
                          }s ease-in-out infinite`,
                          animationDelay: `${(index % 12) * 0.1}s`,
                        }}
                      />
                    ))}
                  </div>
                </div>

                <div className="absolute top-8 left-8 glassmorphic rounded-lg p-4 opacity-80 group-hover:opacity-100 transition-opacity">
                  <div className="flex items-center gap-3">
                    <Activity className="w-5 h-5 text-cyan-400" />
                    <div>
                      <div className="text-xs text-gray-400">
                        Real-time Activity
                      </div>
                      <div className="text-lg font-bold font-mono text-cyan-400">
                        2,847
                      </div>
                    </div>
                  </div>
                </div>

                <div className="absolute top-8 right-8 glassmorphic rounded-lg p-4 opacity-80 group-hover:opacity-100 transition-opacity">
                  <div className="flex items-center gap-3">
                    <BarChart3 className="w-5 h-5 text-purple-400" />
                    <div>
                      <div className="text-xs text-gray-400">Data Points</div>
                      <div className="text-lg font-bold font-mono text-purple-400">
                        10K+
                      </div>
                    </div>
                  </div>
                </div>

                <div className="absolute bottom-8 left-8 glassmorphic rounded-lg p-4 opacity-80 group-hover:opacity-100 transition-opacity">
                  <div className="flex items-center gap-3">
                    <LineChart className="w-5 h-5 text-green-400" />
                    <div>
                      <div className="text-xs text-gray-400">Accuracy</div>
                      <div className="text-lg font-bold font-mono text-green-400">
                        99.9%
                      </div>
                    </div>
                  </div>
                </div>

                <div className="relative z-10 text-center">
                  <div className="w-24 h-24 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-2xl flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform">
                    <Database className="w-12 h-12" />
                  </div>
                  <div className="text-2xl font-bold mb-2">
                    Platform Dashboard
                  </div>
                  <div className="text-gray-400">
                    Real-time mortgage analytics & insights
                  </div>
                  <div className="mt-4 inline-flex items-center gap-2 text-cyan-400 text-sm">
                    <Sparkles className="w-4 h-4" />
                    <span>Live Demo Available</span>
                  </div>
                </div>

                {[
                  "top-0 left-0",
                  "top-0 right-0",
                  "bottom-0 left-0",
                  "bottom-0 right-0",
                ].map((position, index) => (
                  <div
                    key={`corner-${position}`}
                    className={`absolute ${position} w-4 h-4 border-2 border-cyan-400 opacity-50`}
                    style={{
                      animation: `pulse ${
                        2 + index * 0.5
                      }s ease-in-out infinite`,
                      animationDelay: `${index * 0.3}s`,
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                Icon: Database,
                title: "Real-Time Data Aggregation",
                description:
                  "10,000+ anonymized loans from brokers, lenders, and credit unions. Updated daily, PIPEDA compliant. Automated data pipelines ensure fresh insights without manual intervention.",
                metric: "99.9% Uptime",
                color: "cyan",
                delay: "0.1s",
                details: [
                  "Automated daily updates",
                  "PIPEDA & GDPR compliant",
                  "k-anonymity (k≥50) privacy protection",
                  "Real-time data validation",
                ],
              },
              {
                Icon: Shield,
                title: "AI-Powered Analytics",
                description:
                  "Advanced machine learning models analyze mortgage patterns, predict defaults, and identify market trends. Deep learning algorithms process millions of data points to deliver actionable insights.",
                metric: "85%+ Accuracy",
                color: "purple",
                delay: "0.2s",
                details: [
                  "Machine learning model training",
                  "Predictive analytics engine",
                  "Pattern recognition algorithms",
                  "Real-time inference pipeline",
                ],
              },
              {
                Icon: Code,
                title: "Developer-First API",
                description:
                  "REST API, GraphQL, Python SDK. Built for seamless integration into your platform. Comprehensive documentation, webhooks, and rate limiting included.",
                metric: "< 200ms Response",
                color: "cyan",
                delay: "0.3s",
                details: [
                  "REST & GraphQL endpoints",
                  "Python & JavaScript SDKs",
                  "Webhook support for real-time updates",
                  "Rate limiting & authentication",
                ],
              },
              {
                Icon: TrendingUp,
                title: "Advanced Analytics",
                description:
                  "Default predictions, prepayment modeling, portfolio stress testing. ML-powered insights with configurable models and custom reporting.",
                metric: "85%+ Accuracy",
                color: "purple",
                delay: "0.4s",
                details: [
                  "ML-powered default predictions",
                  "Prepayment behavior modeling",
                  "Portfolio stress testing",
                  "Custom report generation",
                ],
              },
            ].map(
              (
                { Icon, title, description, metric, color, delay, details },
                index
              ) => (
                <div
                  key={title}
                  data-animate
                  id={`feature-${index}`}
                  className={`glassmorphic rounded-2xl p-6 card-hover group relative overflow-hidden ${
                    isVisible[`feature-${index}`] ? "animate-fadeInUp" : ""
                  }`}
                  style={{ animationDelay: delay }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent to-black/20 group-hover:to-black/0 transition-all duration-300" />
                  <div className="relative">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                      style={{
                        background:
                          color === "cyan"
                            ? "rgba(0, 212, 255, 0.2)"
                            : "rgba(139, 92, 246, 0.2)",
                      }}
                    >
                      <Icon
                        className="w-8 h-8"
                        style={{
                          color: colorToHex[color as keyof typeof colorToHex],
                        }}
                      />
                    </div>
                    <h3 className="text-xl font-bold mb-3 group-hover:text-cyan-400 transition-colors">
                      {title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed mb-4">
                      {description}
                    </p>
                    {details && (
                      <ul className="space-y-2 mb-4">
                        {details.map((detail, i) => (
                          <li
                            key={i}
                            className="flex items-start text-xs text-gray-500"
                          >
                            <CheckCircle className="w-3 h-3 mr-2 text-[#00FF88] flex-shrink-0 mt-0.5" />
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div
                      className="font-mono text-sm font-bold flex items-center gap-2 pt-2 border-t border-[#00D4FF]/20"
                      style={{
                        color: colorToHex[color as keyof typeof colorToHex],
                      }}
                    >
                      <ArrowRight className="w-4 h-4" />
                      {metric}
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="relative py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div
            data-animate
            id="how-it-works-title"
            className={
              isVisible["how-it-works-title"] ? "animate-fadeInUp" : ""
            }
          >
            <h2 className="text-[2rem] md:text-[3rem] font-bold text-center mb-4">
              How It <span className="gradient-text">Works</span>
            </h2>
            <p className="text-xl text-gray-400 text-center mb-16">
              From data collection to AI-powered insights
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#00D4FF] via-[#8B5CF6] to-[#00D4FF] transform -translate-x-1/2 hidden md:block" />

            <div className="space-y-16">
              {[
                {
                  step: "01",
                  title: "Data Aggregation",
                  description:
                    "Partner with mortgage brokers, private lenders, and credit unions to collect anonymized loan-level performance data in real-time. Automated ETL pipelines process data from multiple sources, ensuring comprehensive coverage across the Canadian mortgage market.",
                  icon: <Database className="w-8 h-8" />,
                  delay: "0.1s",
                  details: [
                    "Multi-source data integration",
                    "Automated ETL pipelines",
                    "Real-time data processing",
                    "Quality assurance checks",
                  ],
                },
                {
                  step: "02",
                  title: "Anonymization & Validation",
                  description:
                    "Apply k-anonymity (k≥50) to ensure complete privacy protection. Strip all PII while maintaining analytical value. Advanced algorithms preserve data utility for accurate market analysis while guaranteeing individual privacy.",
                  icon: <Lock className="w-8 h-8" />,
                  delay: "0.2s",
                  details: [
                    "k-anonymity (k≥50) privacy",
                    "PII removal & masking",
                    "Data utility preservation",
                    "PIPEDA compliance verified",
                  ],
                },
                {
                  step: "03",
                  title: "AI-Powered Processing",
                  description:
                    "Advanced machine learning models process aggregated data to identify patterns, predict trends, and generate insights. Deep learning algorithms continuously learn from new data to improve accuracy and deliver actionable intelligence.",
                  icon: <Sparkles className="w-8 h-8" />,
                  delay: "0.3s",
                  details: [
                    "Machine learning model inference",
                    "Predictive analytics generation",
                    "Pattern recognition & clustering",
                    "Continuous model refinement",
                  ],
                },
                {
                  step: "04",
                  title: "API Delivery",
                  description:
                    "Access data through REST API, GraphQL, or Python SDK. Build custom analytics, integrate into your platform, or use our dashboards. Sub-200ms response times with 99.9% uptime guarantee.",
                  icon: <Code className="w-8 h-8" />,
                  delay: "0.4s",
                  details: [
                    "REST & GraphQL APIs",
                    "Python & JavaScript SDKs",
                    "Real-time webhooks",
                    "Custom dashboard access",
                  ],
                },
              ].map((item, index) => (
                <div
                  key={index}
                  data-animate
                  id={`how-it-works-${index}`}
                  className={`grid md:grid-cols-2 gap-8 items-center ${
                    isVisible[`how-it-works-${index}`] ? "animate-fadeInUp" : ""
                  }`}
                  style={{ animationDelay: item.delay }}
                >
                  <div
                    className={`${
                      index % 2 === 1
                        ? "md:col-start-2 md:text-left"
                        : "md:text-right"
                    }`}
                  >
                    <div className="inline-block px-4 py-2 bg-[#00D4FF]/10 border border-[#00D4FF]/30 rounded-full text-[#00D4FF] text-sm font-mono mb-4">
                      {item.step}
                    </div>
                    <h3 className="text-2xl font-bold mb-3">{item.title}</h3>
                    <p className="text-gray-400 mb-4">{item.description}</p>
                    {item.details && (
                      <ul
                        className={`space-y-2 ${
                          index % 2 === 1
                            ? "text-left"
                            : "text-right md:text-right"
                        }`}
                      >
                        {item.details.map((detail, i) => (
                          <li
                            key={i}
                            className="flex items-center text-sm text-gray-500"
                          >
                            {index % 2 === 1 ? (
                              <>
                                <CheckCircle className="w-4 h-4 mr-2 text-[#00FF88] flex-shrink-0" />
                                <span>{detail}</span>
                              </>
                            ) : (
                              <>
                                <span className="ml-auto">{detail}</span>
                                <CheckCircle className="w-4 h-4 ml-2 text-[#00FF88] flex-shrink-0" />
                              </>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div
                    className={`${
                      index % 2 === 1 ? "md:col-start-1 md:row-start-1" : ""
                    }`}
                  >
                    <div className="relative">
                      <div className="w-full p-8 glassmorphic rounded-2xl border border-[#00D4FF]/30 flex items-center justify-center">
                        <div className="w-24 h-24 bg-gradient-to-br from-[#00D4FF]/20 to-[#8B5CF6]/20 rounded-2xl flex items-center justify-center">
                          {item.icon}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section
        id="use-cases"
        className="relative py-24 px-6 bg-gradient-to-b from-[#1A1F3A] to-[#0A0E27]"
      >
        <div className="max-w-7xl mx-auto">
          <div
            data-animate
            id="use-cases-title"
            className={isVisible["use-cases-title"] ? "animate-fadeInUp" : ""}
          >
            <h2 className="text-[2rem] md:text-[3rem] font-bold text-center mb-4">
              Built For <span className="gradient-text">Real-World</span> Use
              Cases
            </h2>
            <p className="text-xl text-gray-400 text-center mb-16">
              Serving alternative lenders, mortgage funds, and PropTech
              innovators
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                Icon: Activity,
                title: "Alternative Lenders",
                description:
                  "Benchmark your default rates against market averages. Access real-time prepayment data and delinquency trends. Make data-driven underwriting decisions with comprehensive market intelligence.",
                features: [
                  "Competitive intelligence & benchmarking",
                  "Real-time default rate tracking",
                  "Prepayment behavior analysis",
                  "Risk assessment & portfolio analytics",
                  "Market trend identification",
                ],
                color: "cyan",
                delay: "0.1s",
              },
              {
                Icon: Code,
                title: "PropTech & FinTech",
                description:
                  "Integrate mortgage data into your platform via API. Build better products with real-time market intelligence. Developer-first architecture with comprehensive SDKs and webhook support.",
                features: [
                  "REST & GraphQL APIs",
                  "Python & JavaScript SDKs",
                  "Webhook integrations for real-time updates",
                  "Custom data filtering & aggregation",
                  "White-label dashboard options",
                ],
                color: "purple",
                delay: "0.2s",
              },
              {
                Icon: TrendingUp,
                title: "Mortgage Funds & Analysts",
                description:
                  "Access institutional-grade analytics for portfolio analysis. Make data-driven decisions with comprehensive market insights, stress testing, and performance forecasting.",
                features: [
                  "Custom report generation",
                  "Portfolio stress testing",
                  "Performance tracking & benchmarking",
                  "ML-powered default predictions",
                  "Market trend analysis",
                ],
                color: "green",
                delay: "0.3s",
              },
            ].map((useCase, index) => (
              <div
                key={index}
                data-animate
                id={`use-case-${index}`}
                className={`glassmorphic rounded-2xl p-8 card-hover ${
                  isVisible[`use-case-${index}`] ? "animate-fadeInUp" : ""
                }`}
                style={{ animationDelay: useCase.delay }}
              >
                <div
                  className={`w-16 h-16 rounded-xl flex items-center justify-center mb-6 ${
                    useCase.color === "cyan"
                      ? "bg-[#00D4FF]/20"
                      : useCase.color === "purple"
                      ? "bg-[#8B5CF6]/20"
                      : "bg-[#00FF88]/20"
                  }`}
                >
                  <useCase.Icon
                    className={`w-8 h-8 ${
                      useCase.color === "cyan"
                        ? "text-[#00D4FF]"
                        : useCase.color === "purple"
                        ? "text-[#8B5CF6]"
                        : "text-[#00FF88]"
                    }`}
                  />
                </div>
                <h3 className="text-2xl font-bold mb-3">{useCase.title}</h3>
                <p className="text-gray-400 mb-4">{useCase.description}</p>
                <ul className="space-y-2 text-sm text-gray-500">
                  {useCase.features.map((feature, i) => (
                    <li key={i} className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-[#00FF88]" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Deep Dive */}
      <section id="technology" className="relative py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div
            data-animate
            id="technology-title"
            className={isVisible["technology-title"] ? "animate-fadeInUp" : ""}
          >
            <h2 className="text-[2rem] md:text-[3rem] font-bold text-center mb-4">
              Powered by{" "}
              <span className="gradient-text">AI & Machine Learning</span>
            </h2>
            <p className="text-xl text-gray-400 text-center mb-16">
              Intelligent insights, not static reports
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-6">
                Why AI-Powered Analytics Matter
              </h3>
              <p className="text-gray-400 mb-8 text-lg">
                Traditional data platforms provide static reports and basic
                statistics. Crypsey uses advanced machine learning models to
                analyze patterns, predict trends, and deliver actionable
                intelligence that transforms raw data into strategic insights
                for better decision-making.
              </p>
              <div className="space-y-6">
                {[
                  {
                    Icon: Shield,
                    title: "Predictive Analytics",
                    description:
                      "Machine learning models analyze historical patterns to predict default rates, prepayment behavior, and market trends. Get forward-looking insights that help you make data-driven decisions before market shifts occur.",
                    color: "purple",
                  },
                  {
                    Icon: Lock,
                    title: "Advanced Pattern Recognition",
                    description:
                      "Deep learning algorithms identify complex patterns across millions of data points that human analysts would miss. Discover hidden correlations and market signals that give you a competitive edge.",
                    color: "cyan",
                  },
                  {
                    Icon: Zap,
                    title: "Real-Time Intelligence",
                    description:
                      "The only AI-powered mortgage analytics platform in Canada with real-time model inference. Get instant insights as new data arrives, enabling faster decision-making and better risk management.",
                    color: "green",
                  },
                ].map((item, index) => (
                  <div key={index} className="flex items-start">
                    <div
                      className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 ${
                        item.color === "purple"
                          ? "bg-[#8B5CF6]/20"
                          : item.color === "cyan"
                          ? "bg-[#00D4FF]/20"
                          : "bg-[#00FF88]/20"
                      }`}
                    >
                      <item.Icon
                        className={`w-6 h-6 ${
                          item.color === "purple"
                            ? "text-[#8B5CF6]"
                            : item.color === "cyan"
                            ? "text-[#00D4FF]"
                            : "text-[#00FF88]"
                        }`}
                      />
                    </div>
                    <div>
                      <h4 className="text-xl font-bold mb-2">{item.title}</h4>
                      <p className="text-gray-400">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="p-8 glassmorphic rounded-2xl border border-[#8B5CF6]/30">
                <div className="space-y-4 font-mono text-sm">
                  <div className="text-[#00D4FF]">
                    // AI Model Inference Process
                  </div>
                  <div className="text-gray-400">
                    <span className="text-[#8B5CF6]">async function</span>{" "}
                    <span className="text-[#00FF88]">generateInsights</span>
                    (data) {"{"}
                  </div>
                  <div className="text-gray-400 pl-4">
                    <span className="text-[#FFB800]">const</span> features =
                    preprocessData(data);
                  </div>
                  <div className="text-gray-400 pl-4">
                    <span className="text-[#FFB800]">const</span> predictions =
                    <span className="text-[#00D4FF]"> await</span>{" "}
                    mlModel.predict(features);
                  </div>
                  <div className="text-gray-400 pl-4">
                    <span className="text-[#FFB800]">const</span> insights =
                    analyzePatterns(predictions);
                  </div>
                  <div className="text-gray-400 pl-4">
                    <span className="text-[#00D4FF]">return</span>{" "}
                    generateReport(insights);
                  </div>
                  <div className="text-gray-400">{"}"}</div>
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-[#8B5CF6]/20 rounded-full blur-3xl" />

              {/* Additional Technical Details */}
              <div className="mt-6 p-6 glassmorphic rounded-xl border border-[#00D4FF]/20">
                <h4 className="text-lg font-bold mb-3 text-[#00D4FF]">
                  AI Model Specifications
                </h4>
                <div className="space-y-2 text-sm text-gray-400">
                  <div className="flex justify-between">
                    <span>ML Framework:</span>
                    <span className="text-[#00FF88] font-mono">
                      TensorFlow/PyTorch
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Inference Speed:</span>
                    <span className="text-[#00FF88] font-mono">&lt;200ms</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Model Accuracy:</span>
                    <span className="text-[#00FF88] font-mono">85%+</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Training Data:</span>
                    <span className="text-[#00FF88] font-mono">10K+ Loans</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Real-Time Updates:</span>
                    <span className="text-[#00FF88] font-mono">Yes</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="relative py-24 px-6 bg-gradient-to-b from-[#1A1F3A] to-[#0A0E27]">
        <div className="max-w-7xl mx-auto">
          <div
            data-animate
            id="benefits-title"
            className={isVisible["benefits-title"] ? "animate-fadeInUp" : ""}
          >
            <h2 className="text-[2rem] md:text-[3rem] font-bold text-center mb-4">
              Why Choose <span className="gradient-text">Crypsey</span>
            </h2>
            <p className="text-xl text-gray-400 text-center mb-16">
              Built for Canadian mortgage professionals who demand accuracy and
              transparency
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                Icon: Shield,
                title: "Trust Through Intelligence",
                description:
                  "Every insight is powered by advanced machine learning models trained on real mortgage data. Transparent AI explanations show you how predictions are made.",
                color: "purple",
                delay: "0.1s",
              },
              {
                Icon: Zap,
                title: "Real-Time Insights",
                description:
                  "Access up-to-date mortgage performance data with daily updates. Make decisions based on current market conditions, not outdated reports.",
                color: "cyan",
                delay: "0.2s",
              },
              {
                Icon: Lock,
                title: "Privacy-First Design",
                description:
                  "k-anonymity (k≥50) ensures complete privacy protection. We strip all personally identifiable information while preserving analytical value.",
                color: "green",
                delay: "0.3s",
              },
              {
                Icon: Code,
                title: "Developer-Friendly",
                description:
                  "REST API, GraphQL, and Python SDK make integration seamless. Comprehensive documentation and webhook support included.",
                color: "cyan",
                delay: "0.4s",
              },
              {
                Icon: TrendingUp,
                title: "Advanced Analytics",
                description:
                  "ML-powered default predictions, prepayment modeling, and portfolio stress testing. Get insights that go beyond basic reporting.",
                color: "purple",
                delay: "0.5s",
              },
              {
                Icon: Activity,
                title: "Proven Reliability",
                description:
                  "99.9% uptime SLA with sub-200ms response times. Enterprise-grade infrastructure designed for mission-critical applications.",
                color: "green",
                delay: "0.6s",
              },
            ].map((benefit, index) => (
              <div
                key={index}
                data-animate
                id={`benefit-${index}`}
                className={`glassmorphic rounded-2xl p-6 card-hover ${
                  isVisible[`benefit-${index}`] ? "animate-fadeInUp" : ""
                }`}
                style={{ animationDelay: benefit.delay }}
              >
                <div
                  className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 ${
                    benefit.color === "cyan"
                      ? "bg-[#00D4FF]/20"
                      : benefit.color === "purple"
                      ? "bg-[#8B5CF6]/20"
                      : "bg-[#00FF88]/20"
                  }`}
                >
                  <benefit.Icon
                    className={`w-7 h-7 ${
                      benefit.color === "cyan"
                        ? "text-[#00D4FF]"
                        : benefit.color === "purple"
                        ? "text-[#8B5CF6]"
                        : "text-[#00FF88]"
                    }`}
                  />
                </div>
                <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      {/* <section
        id="pricing"
        className="relative py-24 px-6 bg-gradient-to-b from-[#0A0E27] to-[#1A1F3A]"
      >
        <div className="max-w-7xl mx-auto">
          <div
            data-animate
            id="pricing-title"
            className={isVisible["pricing-title"] ? "animate-fadeInUp" : ""}
          >
            <h2 className="text-[2rem] md:text-[3rem] font-bold text-center mb-4">
              Simple, <span className="gradient-text">Transparent</span> Pricing
            </h2>
            <p className="text-xl text-gray-400 text-center mb-16">
              Choose the plan that fits your needs. All plans include AI-powered
              analytics.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {pricingTiers.map(
              (
                {
                  name,
                  price,
                  period,
                  description,
                  features,
                  cta,
                  highlighted,
                  Icon,
                  color,
                  delay,
                  badge,
                },
                index
              ) => (
                <div
                  key={name}
                  data-animate
                  id={`pricing-${index}`}
                  className={`relative p-8 rounded-2xl backdrop-blur-sm transition-all transform hover:-translate-y-2 ${
                    highlighted
                      ? "bg-gradient-to-b from-[#00D4FF]/20 to-[#8B5CF6]/20 border-2 border-[#00D4FF] shadow-2xl shadow-[#00D4FF]/30"
                      : "glassmorphic border border-[#00D4FF]/20"
                  } ${isVisible[`pricing-${index}`] ? "animate-fadeInUp" : ""}`}
                  style={{ animationDelay: delay }}
                >
                  {badge && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-[#00D4FF] to-[#8B5CF6] rounded-full text-sm font-bold">
                      {badge}
                    </div>
                  )}
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto"
                    style={{
                      background:
                        color === "cyan"
                          ? "rgba(0, 212, 255, 0.2)"
                          : color === "purple"
                          ? "rgba(139, 92, 246, 0.2)"
                          : "rgba(0, 255, 136, 0.2)",
                      border: `2px solid ${
                        color === "cyan"
                          ? "rgba(0, 212, 255, 0.3)"
                          : color === "purple"
                          ? "rgba(139, 92, 246, 0.3)"
                          : "rgba(0, 255, 136, 0.3)"
                      }`,
                    }}
                  >
                    <Icon
                      className="w-8 h-8"
                      style={{
                        color: colorToHex[color as keyof typeof colorToHex],
                      }}
                    />
                  </div>
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold mb-2">{name}</h3>
                    <p className="text-gray-400 text-sm">{description}</p>
                  </div>

                  <div className="mb-6">
                    <div className="text-4xl font-bold text-[#00D4FF]">
                      {price}
                      {price !== "Custom" && (
                        <span className="text-lg text-gray-400">/mo</span>
                      )}
                    </div>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {features.map(({ text, included }) => (
                      <li key={text} className="flex items-start">
                        <CheckCircle className="w-5 h-5 mr-2 text-[#00FF88] flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-300">{text}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    className={`w-full py-3 rounded-lg font-semibold transition-all ${
                      highlighted
                        ? "bg-gradient-to-r from-[#00D4FF] to-[#8B5CF6] hover:shadow-lg hover:shadow-[#00D4FF]/50"
                        : price === "Custom"
                        ? "bg-[#00D4FF]/10 border border-[#00D4FF] hover:bg-[#00D4FF]/20"
                        : "bg-[#00D4FF]/10 border border-[#00D4FF] hover:bg-[#00D4FF]/20"
                    }`}
                    onClick={() => {
                      if (price === "Custom") {
                        openForm("call");
                      } else {
                        openForm("trial");
                      }
                    }}
                  >
                    {cta}
                  </button>
                </div>
              )
            )}
          </div>
        </div>
      </section> */}

      {/* API Preview Section */}
      <section id="api" className="relative py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-[2rem] md:text-[3rem] font-bold mb-6">
                Developer-First <span className="gradient-text">API</span>
              </h2>
              <p className="text-xl text-gray-400 mb-8">
                Integrate mortgage data into your platform in minutes. RESTful
                API with comprehensive documentation, Python SDK, and GraphQL
                support. Built for developers who value speed, reliability, and
                clear documentation.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  "Sub-200ms response times with caching",
                  "99.9% uptime SLA with monitoring",
                  "Comprehensive API documentation with examples",
                  "Webhook support for real-time updates",
                  "Rate limiting & authentication included",
                  "GraphQL for flexible queries",
                  "Python & JavaScript SDKs available",
                  "Postman collection & code samples",
                ].map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <CheckCircle className="w-5 h-5 mr-3 text-[#00FF88] flex-shrink-0" />
                    <span className="text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>

              <a
                href="/docs"
                className="inline-block px-8 py-3 glassmorphic border border-[#00D4FF] rounded-lg font-semibold hover:bg-[#00D4FF]/20 transition-all"
              >
                View Full Documentation
              </a>
            </div>

            <div className="p-6 glassmorphic rounded-2xl border border-[#00D4FF]/30">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-mono text-[#00D4FF]">
                  API Request
                </span>
                <div className="flex space-x-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-[#FFB800]" />
                  <div className="w-3 h-3 rounded-full bg-[#00FF88]" />
                </div>
              </div>
              <pre className="font-mono text-xs overflow-x-auto">
                <code className="text-gray-300">
                  {`GET /api/v1/loans?
  location=toronto&
  ltv_min=0.7&
  ltv_max=0.8&
  credit_score_min=680

{
  "data": [
    {
      "loan_id": "a9f2c...",
      "amount": 450000,
      "interest_rate": 7.85,
      "ltv": 0.75,
      "location": "M5V",
      "credit_score_range": "680-720",
      "status": "current",
      "ai_confidence": 0.92,
      "predicted_risk": "low"
    }
  ],
  "meta": {
    "count": 127,
    "model_version": "v2.1",
    "prediction_accuracy": 0.87
  }
}`}
                </code>
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section
        id="blog"
        className="relative py-24 px-6 bg-gradient-to-b from-[#0A0E27] to-[#1A1F3A]"
      >
        <div className="max-w-7xl mx-auto">
          <div
            data-animate
            id="blog-title"
            className={isVisible["blog-title"] ? "animate-fadeInUp" : ""}
          >
            <h2 className="text-[2rem] md:text-[3rem] font-bold text-center mb-4">
              Latest <span className="gradient-text">Insights</span>
            </h2>
            <p className="text-xl text-gray-400 text-center mb-16">
              Stay updated with mortgage market trends, AI technology, and
              industry analysis
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {isBlogLoading ? (
              Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={`blog-skeleton-${index}`}
                  className="glassmorphic rounded-2xl p-6 overflow-hidden relative"
                >
                  <div className="absolute inset-0 shimmer opacity-20" />
                  <div className="relative z-10 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="h-6 w-24 bg-white/10 rounded-full" />
                      <div className="h-4 w-20 bg-white/10 rounded" />
                    </div>
                    <div className="h-6 w-5/6 bg-white/10 rounded" />
                    <div className="space-y-2">
                      <div className="h-4 w-full bg-white/10 rounded" />
                      <div className="h-4 w-11/12 bg-white/10 rounded" />
                      <div className="h-4 w-4/5 bg-white/10 rounded" />
                    </div>
                    <div className="pt-4 border-t border-[#00D4FF]/20 flex items-center justify-between">
                      <div className="h-4 w-28 bg-white/10 rounded" />
                      <div className="h-4 w-20 bg-white/10 rounded" />
                    </div>
                  </div>
                </div>
              ))
            ) : blogError || blogCards.length === 0 ? (
              <div className="lg:col-span-3 glassmorphic rounded-2xl p-8 text-center">
                <div className="mb-6">
                  <Globe className="w-16 h-16 mx-auto mb-4 text-gray-500 opacity-50" />
                  <h3 className="text-xl font-bold mb-2 text-gray-300">
                    {blogError
                      ? "Unable to Load Blog Posts"
                      : "No Blog Posts Available"}
                  </h3>
                  <p className="text-gray-400 mb-4">
                    {blogError ||
                      "Check back soon for the latest insights and updates."}
                  </p>
                </div>
                <a
                  href={GHOST_API_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 glassmorphic border border-[#00D4FF] rounded-lg font-semibold hover:bg-[#00D4FF]/20 transition-all group"
                >
                  <span>Visit Blog</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            ) : (
              blogCards.map((post) => (
                <a
                  key={post.id}
                  href={post.href}
                  target="_blank"
                  rel="noreferrer"
                  data-animate
                  id={post.id}
                  className={`glassmorphic rounded-2xl p-6 card-hover group cursor-pointer overflow-hidden relative block ${
                    isVisible[post.id] ? "animate-fadeInUp" : ""
                  }`}
                  style={{ animationDelay: post.delay }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent to-black/20 group-hover:to-black/0 transition-all duration-300" />
                  <div className="relative z-10">
                    {post.featureImage ? (
                      <div className="mb-5 overflow-hidden rounded-xl border border-white/10">
                        <img
                          src={post.featureImage}
                          alt=""
                          className="h-40 w-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                          loading="lazy"
                        />
                      </div>
                    ) : null}

                    <div className="flex items-center justify-between mb-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          post.color === "cyan"
                            ? "bg-[#00D4FF]/20 text-[#00D4FF] border border-[#00D4FF]/30"
                            : post.color === "purple"
                            ? "bg-[#8B5CF6]/20 text-[#8B5CF6] border border-[#8B5CF6]/30"
                            : "bg-[#00FF88]/20 text-[#00FF88] border border-[#00FF88]/30"
                        }`}
                      >
                        {post.category}
                      </span>
                      {post.date ? (
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Calendar className="w-3 h-3" />
                          <span>{post.date}</span>
                        </div>
                      ) : null}
                    </div>

                    <h3 className="text-xl font-bold mb-3 group-hover:text-cyan-400 transition-colors line-clamp-2">
                      {post.title}
                    </h3>

                    <p className="text-gray-400 text-sm leading-relaxed mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>

                    <div className="flex items-center justify-between pt-4 border-t border-[#00D4FF]/20">
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Users className="w-3 h-3" />
                        <span>{post.author}</span>
                      </div>
                      {post.readTime ? (
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span>{post.readTime}</span>
                        </div>
                      ) : null}
                    </div>

                    <div className="mt-4 flex items-center gap-2 text-sm text-cyan-400 group-hover:gap-3 transition-all">
                      <span className="font-semibold">Read More</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </a>
              ))
            )}
          </div>

          <div className="mt-12 text-center">
            <a
              href={GHOST_API_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 glassmorphic border border-[#00D4FF] rounded-lg font-semibold hover:bg-[#00D4FF]/20 transition-all group"
            >
              <span>View All Articles</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#00D4FF]/10 to-[#8B5CF6]/10" />
        <div className="absolute inset-0 backdrop-blur-3xl" />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="text-[2rem] md:text-[3rem] font-bold mb-6 text-center">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join the future of Canadian mortgage data. Start your free trial
            today.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="px-8 py-4 bg-gradient-to-r from-[#00D4FF] to-[#8B5CF6] rounded-lg font-semibold text-lg hover:shadow-2xl hover:shadow-[#00D4FF]/50 transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2"
            >
              View Dashboard
              <ArrowRight className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => openForm("demo")}
              className="px-8 py-4 bg-transparent border-2 border-[#00D4FF] rounded-lg font-semibold text-lg hover:bg-[#00D4FF]/10 transition-all"
            >
              Schedule Demo
            </button>
          </div>

          <p className="text-sm text-gray-400">
            No credit card required • 7-day free trial • Cancel anytime
          </p>
        </div>
      </section>

      <MortgageChainFooter
        onScheduleCall={() => openForm("call")}
        onViewDeck={() => openForm("call")}
      />

      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm animate-fadeInUp">
          <div className="glassmorphic-strong rounded-3xl p-12 max-w-lg w-full text-center relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none">
              {Array.from({ length: 50 }).map((_, index) => (
                <div
                  key={`confetti-${index}`}
                  className="absolute w-2 h-2 rounded-full"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: "-10%",
                    background: ["#00D4FF", "#8B5CF6", "#00FF88", "#FFB800"][
                      Math.floor(Math.random() * 4)
                    ],
                    animation: `float ${
                      2 + Math.random() * 3
                    }s ease-out forwards`,
                    animationDelay: `${Math.random() * 0.5}s`,
                  }}
                />
              ))}
            </div>
            <div className="relative z-10">
              <div className="w-24 h-24 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-scaleIn">
                <Check className="w-12 h-12" />
              </div>
              <h3 className="text-3xl font-bold mb-4 gradient-text">
                Success!
              </h3>
              <p className="text-xl text-gray-300 mb-8">
                {formType === "trial"
                  ? "Check your email for your account setup instructions!"
                  : formType === "demo"
                  ? "Thank you for your interest! We'll be in touch within 24 hours to schedule your demo."
                  : "Thank you for your interest! We'll be in touch within 24 hours to schedule your call."}
              </p>
              <button
                type="button"
                onClick={() => setShowSuccessModal(false)}
                className="btn-font bg-gradient-to-r from-cyan-500 to-purple-500 px-8 py-4 rounded-xl font-bold hover-glow"
              >
                Continue Exploring
              </button>
            </div>
          </div>
        </div>
      )}

      <ContactModal
        isOpen={isFormOpen}
        type={formType}
        onClose={closeForm}
        onSubmit={handleFormSubmit}
      />
    </div>
  );
}
